var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { useBoolState } from '@/hooks/state/useBoolState';
import { wrapNotifyFileUploadError } from '@/utils/async/errorHandling';
/**
 * ファイルアップロードに関する処理（単数, 複数 共通）
 * @param options
 * @returns
 */
export const useFileUploadField = (options) => {
    const { fieldKeyNameOfName, fieldKeyNameOfNewId, fieldKeyNameOfPath, fieldName, fileValue, invalid, onChangeFile, onChangeFileCallback, onStartUploadingCallback, onStopUploadingCallback, uploadFile, validateOfNewId, } = options;
    const form = useForm();
    const fieldUnmountRef = useUnmountRef();
    const fieldNameOfNewId = `${fieldName}.${fieldKeyNameOfNewId}`;
    const fieldNameOfName = `${fieldName}.${fieldKeyNameOfName}`;
    const fieldNameOfPath = `${fieldName}.${fieldKeyNameOfPath}`;
    // ローディング制御
    const { boolState: loading, changeFalse: stopLoading, changeTrue: startLoading, } = useBoolState();
    // Field new id
    const { input: { onBlur: onBlurNewId, onChange: onChangeNewId, value: newIdValue }, } = useField(fieldNameOfNewId, {
        validate: (value, allValues, meta) => __awaiter(void 0, void 0, void 0, function* () {
            return validateOfNewId === null || validateOfNewId === void 0 ? void 0 : validateOfNewId(value, allValues, meta ? Object.assign(Object.assign({}, meta), { extra: { fieldUnmountRef } }) : undefined);
        }),
    });
    // Field name
    const { input: { onChange: onChangeName, value: nameValue }, } = useField(fieldNameOfName, {
        subscription: { value: true },
    });
    // Field path
    const { input: { onChange: onChangePath, value: pathValue }, } = useField(fieldNameOfPath, {
        subscription: { value: true },
    });
    /** 選択中のファイル */
    const inputFile = useMemo(() => (fileValue instanceof FileList ? fileValue.item(0) : null), [fileValue]);
    // ファイル変更の監視, アップロード
    useEffect(() => {
        let active = true;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            if (!inputFile) {
                return;
            }
            // callback があれば実行
            const isUploadable = (_a = onChangeFileCallback === null || onChangeFileCallback === void 0 ? void 0 : onChangeFileCallback()) !== null && _a !== void 0 ? _a : true;
            if (invalid || !isUploadable) {
                return;
            }
            const upload = () => __awaiter(void 0, void 0, void 0, function* () {
                startLoading();
                onStartUploadingCallback === null || onStartUploadingCallback === void 0 ? void 0 : onStartUploadingCallback();
                const result = (yield uploadFile(inputFile)) || {};
                if (!result.id) {
                    stopLoading();
                    onStopUploadingCallback === null || onStopUploadingCallback === void 0 ? void 0 : onStopUploadingCallback();
                    return;
                }
                if (active) {
                    form.batch(() => {
                        onChangeName(inputFile.name);
                        onChangePath(result.temporaryUrl || '');
                        onBlurNewId();
                        onChangeNewId(result.id);
                    });
                }
            });
            const uploadFinally = () => {
                if (active) {
                    stopLoading();
                    onStopUploadingCallback === null || onStopUploadingCallback === void 0 ? void 0 : onStopUploadingCallback();
                    onChangeFile === null || onChangeFile === void 0 ? void 0 : onChangeFile(undefined);
                }
            };
            // エラーハンドリング
            yield wrapNotifyFileUploadError({
                callback: upload,
                finallyCallback: uploadFinally,
            });
        }))();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputFile, onStartUploadingCallback, onStopUploadingCallback]);
    return {
        inputFile,
        invalid,
        loading,
        nameValue,
        newIdValue,
        pathValue,
    };
};
